import React, { ReactNode, useEffect } from 'react';
import ReactModal from 'react-modal';
import { useScrollbarWidth } from 'react-use';

import { MODAL_HEIGHT_CSS_VAR } from '../constants/cssVariables';
import { MODAL_TRASITION_MS } from '../constants/transitions';
import * as styles from '../styles/Modal.styles';

const setModalHeight = () =>
  document.documentElement.style.setProperty(MODAL_HEIGHT_CSS_VAR, `${window.innerHeight}px`);

export type ModalProps = {
  isModalOpen: boolean;
  closeModal: () => void;
  onAfterClose?: () => void;
  contentLabel: string;
  children: ReactNode;
};

const MODAL_CONTENT_CLASS_NAMES: styles.ModalGlobalStylesProps['contentClassNames'] = {
  base: 'modal-content',
  afterOpen: 'modal-content--afterOpen',
  beforeClose: 'modal-content--beforeClose',
};

const MODAL_OVERLAY_CLASS_NAMES: styles.ModalGlobalStylesProps['overlayClassNames'] = {
  base: 'modalOverlay',
  afterOpen: 'modal-overlay--afterOpen',
  beforeClose: 'modal-overlay--beforeClose',
};

const ModalGlobalStyles = styles.ModalGlobalStyles({
  contentClassNames: MODAL_CONTENT_CLASS_NAMES,
  overlayClassNames: MODAL_OVERLAY_CLASS_NAMES,
  tranistionMS: MODAL_TRASITION_MS,
});

export const Modal = ({
  isModalOpen,
  closeModal,
  onAfterClose,
  contentLabel,
  children,
}: ModalProps) => {
  const scrollBarWidth = useScrollbarWidth();
  ReactModal.setAppElement('#NOTHSPageWrapper');

  useEffect(() => {
    const handler = () => {
      // Note: value not always correctly set/updated on Android within Testcafe tests on Browserstack...
      setModalHeight();
    };

    handler();
    window.addEventListener('resize', handler);

    return () => window.removeEventListener('resize', handler);
  }, []);

  return (
    <>
      <ModalGlobalStyles scrollBarWidth={scrollBarWidth || 0} />
      <ReactModal
        closeTimeoutMS={MODAL_TRASITION_MS}
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        onAfterClose={onAfterClose}
        onAfterOpen={() => {
          /* Note: The innerHeight value returned within the hook above is not always correct when running the Testcafe
          tests on Android within Browserstack, therefore explicitly redefine it here */
          setModalHeight();
        }}
        className={MODAL_CONTENT_CLASS_NAMES}
        overlayClassName={MODAL_OVERLAY_CLASS_NAMES}
        contentLabel={contentLabel}
      >
        <div css={styles.modalContent}>{children}</div>
      </ReactModal>
    </>
  );
};
